import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs/Subscription';
import { AccessoryCategory, Constants, PositionValues } from '../../../core/Constants';
import { BRAND } from '../../../core/DI';
import { GenericSummaryCardDtoResponse } from '../../../core/models/generic-summary-card.dto-response';
import { MakeYearModel } from '../../../core/models/make-year-model.model';
import { ProductCardDtoResponse } from '../../../core/models/product-card-dto-response';
import { ProductDetailsModel } from '../../../core/models/product-details-model.model';
import { ProductIdsModel } from '../../../core/models/product-ids.model';
import { RouteContext } from '../../../core/models/route-context';
import { User } from '../../../core/User';
import { PackageBuilderService } from '../../../package-builder/package-builder.service';
import { ProductDetailsBaseComponent } from '../../../product-details/product-details.base-component';
import { HardwareService } from '../../hardware.service';
import { Helper } from '../../helper';
import { NormalizeQtyPipe } from '../../pipe/normalize-qty.pipe';
import { SearchTextService } from '../../search-text.service';
import { ProductZoomService } from 'app/shared/notes/product-zoom.service';
import { PackageKitService } from 'app/shared/packagekit.service';
import { LeadTimeDtoResponse } from '../../../core/models/lead-time-dto-response';

@Component({
  selector: 'app-hardware-details-modal',
  templateUrl: './hardware-details-modal.component.html',
  styleUrls: ['./hardware-details-modal.component.css']
})

export class HardwareDetailsModalComponent extends ProductDetailsBaseComponent implements OnInit, OnDestroy {
  @Input() context: RouteContext;
  @Input() hardware: ProductCardDtoResponse;
  @Input() card: GenericSummaryCardDtoResponse;
  @Input() keys: GenericSummaryCardDtoResponse[];
  @Input() anchorClass = '';
  @Input() mtlWheelDrillLeadTime: LeadTimeDtoResponse;
  @Output() change = new EventEmitter();
  @Output() selectProductComplete = new EventEmitter();
  @Output() productRemoved = new EventEmitter();

  public modalInstance;
  showWaiting: boolean;
  showError: boolean;
  isInB2b: boolean;
  brand: string;
  currentImage: string;
  imageList: string[] = [];
  packageBuilderService: PackageBuilderService;
  isInPackageBuilder: boolean;
  qty = 0;
  totalRetail: number;
  totalPrice: number;
  selected: boolean;
  isFrontSpecific: boolean;
  isRearSpecific: boolean;
  frontWheelQty: number;
  rearWheelQty: number;
  category: AccessoryCategory;
  brandLogoURL: string;
  closeResult: string;
  hardwareProduct: ProductDetailsModel = new ProductDetailsModel();
  hardwareSvc: HardwareService;
  mym: MakeYearModel;
  subs: Subscription[] = [];
  searchTextService: SearchTextService;
  packageKitService: PackageKitService;

  constructor(private hardwareService: HardwareService,
    private translateService: TranslateService,
    private normalizeQtyPipe: NormalizeQtyPipe,
    private injector: Injector,
    @Inject(BRAND) brand: string,
    private modalService: NgbModal,
    private productZoomService: ProductZoomService) {
    super();
    this.brand = brand;
    this.isInB2b = brand === Constants.BRAND_B2B;
    this.isInPackageBuilder = true;
  }

  ngOnInit() {
    if (this.context === RouteContext.PACKAGE_BUILDER && this.card.productType !== "Kit") {
      this.packageBuilderService = this.injector.get<PackageBuilderService>(PackageBuilderService);
      this.subs.push(this.packageBuilderService.onPackageChange.subscribe(pbModel => {
        if (!this.frontWheelQty) {
          this.frontWheelQty = pbModel.front.wheel.quantity;
        }
        if (!this.rearWheelQty && pbModel.rear && pbModel.rear.wheel) {
          this.rearWheelQty = pbModel.rear.wheel.quantity;
        }
        let allHardware = pbModel.front.hardwares;
        allHardware = allHardware.concat(pbModel.rear.hardwares);

        const index = allHardware.length > 0 ?
          allHardware.map(hardware => hardware != null ? hardware.id : '').indexOf(this.hardware.longSKU) : -1;

        if (index !== -1) {
          this.selected = true;
          this.qty = allHardware[index].quantity;
          this.totalRetail = this.qty * parseFloat(this.hardware.retailPrice);
          this.totalPrice = this.qty * parseFloat(this.hardware.customerPrice);
        } else {
          this.selected = false;
          this.qty = 1;
        }

        this.category = this.hardware.details.details.find(d => d.label === 'Type').accessoryType;
      }, Helper.handleError));
      const BreakPointDummyLine = 0;
    } else if (this.context === RouteContext.PRODUCTS_BY_SPEC) {
      this.category = this.card.frontProduct.accessoryType;
    }

    this.totalRetail = parseFloat(this.hardware.retailPrice) * this.qty;
    this.totalPrice = parseFloat(this.hardware.customerPrice) * this.qty;
    this.isFrontSpecific = this.hardware.position === PositionValues.FRONT;
    this.isRearSpecific = this.hardware.position === PositionValues.REAR;
    // djb:yagni-no need to show/hide as these categories cannot open the modal 
    //this.showInventory = !(this.category === AccessoryCategory.Mounting || this.category === AccessoryCategory.Studding);

  }

  ngOnDestroy(): void {
    //Helper.log('hdm.onDestroy');
    this.subs.forEach(sub => sub.unsubscribe());
  }

  load(): void {
    this.hardwareSvc = this.injector.get<HardwareService>(HardwareService);
    this.searchTextService = this.injector.get<SearchTextService>(SearchTextService);
    this.packageKitService = this.injector.get<PackageKitService>(PackageKitService);
    if (this.context === RouteContext.PRODUCTS_BY_SPEC) {
      this.subs.push(this.hardwareSvc.getProductDetailsBySpecs(this.hardware.longSKU, null).subscribe(responseProduct => {
        if (responseProduct !== null) {
          this.hardwareProduct.product = responseProduct;
          super.addSkuToDetails(responseProduct);
          this.getImageList();
          this.brandLogoURL = Helper.getLogoURL(responseProduct.productType, responseProduct.productBrand);
          this.showWaiting = false;
        } else {
          this.showWaiting = false;
          this.showError = true;
        }
      },
        error => {
          this.showWaiting = false;
          this.showError = true;
        }));
    } else if (this.context === RouteContext.SEARCH_BY_TEXT) {
      this.subs.push(this.searchTextService.getProductDetailsBySpecs(this.hardware.longSKU).subscribe(responseProduct => {
        if (responseProduct !== null) {
          this.hardwareProduct.product = responseProduct;
          super.addSkuToDetails(responseProduct);
          this.getImageList();
          this.brandLogoURL = Helper.getLogoURL(responseProduct.productType, responseProduct.productBrand);
          this.showWaiting = false;
        } else {
          this.showWaiting = false;
          this.showError = true;
        }
      },
        error => {
          this.showWaiting = false;
          this.showError = true;
        }));
    } else if (this.context === RouteContext.SEARCH_BY_VEHICLE_FOR_ACCESSORY) {
      this.mym = this.hardwareSvc.getMakeYearModel();
      this.subs.push(this.hardwareSvc.getProductDetailsBySpecs(this.hardware.longSKU, this.mym).subscribe(responseProduct => {
        if (responseProduct !== null) {
          this.hardwareProduct.product = responseProduct;
          super.addSkuToDetails(responseProduct);
          this.getImageList();
          this.brandLogoURL = Helper.getLogoURL(responseProduct.productType, responseProduct.productBrand);
          this.showWaiting = false;
        } else {
          this.showWaiting = false;
          this.showError = true;
        }
      },
        error => {
          this.showWaiting = false;
          this.showError = true;
        }));
    } else if (this.context === RouteContext.PACKAGE_BUILDER) {
      if (this.card.productType === "Kit") {
        this.subs.push(this.packageKitService.getProductDetailsBySpecs(this.hardware.longSKU).subscribe(responseProduct => {
          if (responseProduct !== null) {
            this.hardwareProduct.product = responseProduct;
            super.addSkuToDetails(responseProduct);
            this.getImageList();
            this.brandLogoURL = Helper.getLogoURL(responseProduct.productType, responseProduct.productBrand);
            this.showWaiting = false;
          } else {
            this.showWaiting = false;
            this.showError = true;
          }
        },
          error => {
            this.showWaiting = false;
            this.showError = true;
          }));
      }
      else {
        let wheelIds = new ProductIdsModel();
        wheelIds = this.packageBuilderService.getWheelIds();
        this.mym = this.packageBuilderService.getMakeYearModel();
        this.subs.push(this.hardwareSvc.getProductDetails(this.hardware.longSKU, this.mym, wheelIds.front, wheelIds.rear !== '' ? wheelIds.rear : '').subscribe(responseProduct => {
          if (responseProduct !== null) {
            this.hardwareProduct.product = responseProduct;
            super.addSkuToDetails(responseProduct);
            this.getImageList();
            this.brandLogoURL = Helper.getLogoURL(responseProduct.productType, responseProduct.productBrand);
            this.showWaiting = false;
          } else {
            this.showWaiting = false;
            this.showError = true;
          }
        },
          error => {
            this.showWaiting = false;
            this.showError = true;
          }));
      }
    }
  }

  open(content) {
    this.showWaiting = true;
    this.load();
    // djb:setup prev/next buttons/swipe/arrows
    Helper.SkuList.setIndex(this.hardware.longSKU);
    this.modalInstance = this.modalService.open(content, { windowClass: 'hardware-details-modal' });
    this.modalInstance.result.then((result) => {
      this.closeResult = 'Closed with: ${result}';
    }, (reason) => {
      this.closeResult = 'Dismissed ${this.getDismissReason(reason)}';
    });
  }

  handleSelectProductComplete(): void {
    this.modalInstance.close();
    //notify parent component that user selected this product
    this.selectProductComplete.emit();
  }

  handleProductRemoved(): void {
    //user has chosen to remove the product from selection
    this.modalInstance.close();
    this.productRemoved.emit();
  }

  getImageList(): void {
    if (this.hardwareProduct && this.hardwareProduct.product.productImages) {
      this.imageList = this.hardwareProduct.product.productImages.map(pi => pi.imageUrl);
      this.currentImage = this.imageList[0];
    }
  }

  openProductZoomDialog(): void {
    this.productZoomService.showProductZoom(this.currentImage, 'FC.ProductWarningsNotes.Continue', 'pb-product-zoom');
  }

  public allowModalDisplay() {
    return (this.category !== AccessoryCategory.Mounting && this.category !== AccessoryCategory.Studding);
  }

  public stateChange() {
    Helper.log("hdm.stateChange");
  }

  get showRetailPrice(): boolean {
    return User.showRetailPrice;
  }

  get showDealerPrice(): boolean {
    return User.showDealerPrice;
  }

  get showInventory(): boolean {
    return User.showInventory;
  }

  get showCalgaryInventory(): boolean {
    let show: boolean = false;
    if (this.hardwareProduct.product.inventory.onHandQtyCal != null) {
      show = true;
    }
    else if (this.hardwareProduct.product.inventory.inTransitQtyCal != null) {
      show = true;
    }
    return show;
  }

  get showAddCart(): boolean {
    return User.showAddCart;
  }

  showEast(productModel: ProductDetailsModel): boolean {
    let result = false;
    if (productModel.product.inventory.onHandQtyPic !== null && productModel.product.inventory.onHandQtyPic != 0)
      result = true;
    else if (productModel.product.onHandDetails && productModel.product.onHandDetails.some(d => d.label === "FC.Inventory.AlternateWarehouse.PIC")) {
      productModel.product.onHandDetails.filter(d => d.label === "FC.Inventory.AlternateWarehouse.PIC").forEach(d => {
        if (d.qty !== null && d.qty != 0)
          result = true;
      });
    }
    return result;
  }

    getMTLInventory(productModel: ProductDetailsModel): string {
        let details: string = "";
        if (productModel.product.productType === "Wheel") {
            if (productModel.product.onHandDetails && productModel.product.onHandDetails.length > 0 &&
                productModel.product.onHandDetails.some(d => d.label.endsWith('MTL') || d.label.endsWith('PIC'))) {
                productModel.product.onHandDetails.filter(d => d.label.endsWith('MTL') || d.label.endsWith('PIC')).forEach(d => {
                    var qty = d.qty;
                    if (productModel.product.onHandDetails.some(d => d.label.endsWith('MTL.hidden')))
                        qty += productModel.product.onHandDetails.find(d => d.label.endsWith('MTL.hidden')).qty;
                    var label = this.translateService.instant(d.label.replace('.AltDetail.MTL', '').replace('.AltDetail.PIC', '').replace('.WarehouseDetail.MTL', '').replace('.WarehouseDetail.PIC', ''));
                    if (details.length > 0)
                        details += "<br />";
                    details += `<tr><td class="inv-label" style=" width: 75%; ">${label}</td><td style="text-align:center;" class="inv-item stockLabel-${this.normalizeQtyPipe.transform(qty)}">${this.normalizeQtyPipe.transform(qty)}</td></tr>`;
                });
            } else {

                details += `<tr class=""><td class="inv-label" style=" width: 75%; ">${this.translateService.instant('FC.ProductDetail.Label.OnHand')}</td><td style="text-align:center;" class="inv-item stockLabel-${this.normalizeQtyPipe.transform(productModel.product.inventory.onHandQtyMtl)}">${this.normalizeQtyPipe.transform(productModel.product.inventory.onHandQtyMtl)}</td></tr>`;
            }
        }
        else {
            if (productModel.product.onHandDetails && productModel.product.onHandDetails.length > 0 &&
                productModel.product.onHandDetails.some(d => d.label.endsWith('MTL') || d.label.endsWith('PIC'))) {
                productModel.product.onHandDetails.filter(d => d.label.endsWith('MTL') || d.label.endsWith('PIC')).forEach(d => {
                    var qty = d.qty;
                    if (productModel.product.onHandDetails.some(d => d.label.endsWith('MTL.hidden')))
                        qty += productModel.product.onHandDetails.find(d => d.label.endsWith('MTL.hidden')).qty;
                    var label = this.translateService.instant(d.label.replace('.AltDetail.MTL', '').replace('.AltDetail.PIC', '').replace('.WarehouseDetail.MTL', '').replace('.WarehouseDetail.PIC', ''));
                    if (details.length > 0)
                        details += "<br />";
                    details += `${label}: ${this.normalizeQtyPipe.transform(qty, productModel.product.productType === "Accessory" ? "acc" : null)}  `;
                    //$("#swipeKeyFunction").focus();

                });
            } else {
                let qty: number = null;
                if (productModel.product.inventory.onHandQtyMtl !== null)
                    qty = productModel.product.inventory.onHandQtyMtl;
                if (productModel.product.inventory.onHandQtyPic !== null) {
                    if (qty === null)
                        qty = 0;
                    qty += productModel.product.inventory.onHandQtyPic;
                }

                details += `${this.normalizeQtyPipe.transform(qty, productModel.product.productType === "Accessory" ? "acc" : null)}`;
                //$("#swipeKeyFunction").focus();

            }
        }
        return details;
    }

  getCALInventory(productModel: ProductDetailsModel): string {
    let details: string = "";
    if (productModel.product.onHandDetails && productModel.product.onHandDetails.length > 0 &&
      productModel.product.onHandDetails.some(d => d.label.endsWith('CAL'))) {
      productModel.product.onHandDetails.filter(d => d.label.endsWith('CAL')).forEach(d => {
        var qty = d.qty;
        if (productModel.product.onHandDetails.some(d => d.label.endsWith('CAL.hidden')))
          qty += productModel.product.onHandDetails.find(d => d.label.endsWith('CAL.hidden')).qty;
        var label = this.translateService.instant(d.label.replace('.AltDetail.CAL', '').replace('.WarehouseDetail.CAL', ''));
        details += `${label}: ${this.normalizeQtyPipe.transform(qty, productModel.product.productType === "Accessory" ? "acc" : null)}  `;
        //$("#swipeKeyFunction").focus();

      });
    } else {
      details += `${this.normalizeQtyPipe.transform(productModel.product.inventory.onHandQtyCal, productModel.product.productType === "Accessory" ? "acc" : null)}`;
      //$("#swipeKeyFunction").focus();

    }
    return details;


  }

  //changeSku(sku: string) {
  //  //if (sku.indexOf("_") > 0) {
  //  //  this.product.longSKU = sku.substring(0, sku.indexOf("_"));
  //  //  this.rearProduct.longSKU = sku.substring(sku.indexOf("_") + 1);
  //  //} else
  //  //  this.product.longSKU = sku;
  //  //Helper.SkuList.setIndex(sku);
  //  //this.getHardwareDetails();
  //}

  changeSku(longSKU: string): void {
    this.showWaiting = true;
    this.hardware.longSKU = longSKU;
    // djb:setup prev/next buttons/swipe/arrows
    Helper.SkuList.setIndex(this.hardware.longSKU);
    this.load();

  }

  get canMovePrev(): boolean {
    return Helper.SkuList.canMovePrev;
  }

  get canMoveNext(): boolean {
    return Helper.SkuList.canMoveNext;
  }

  movePrev(source: string): void {
    Helper.log(`${source} movePrev(left) `);
    if (this.canMovePrev) {
      this.changeSku(Helper.SkuList.prev);
    }
  }

  moveNext(source: string): void {
    Helper.log(`${source}: movePrev(right)`);
    if (this.canMoveNext) {
      this.changeSku(Helper.SkuList.next);
    }
  }

  getLabel(label: string) {
    return this.translateService.instant(label).replace("<br/>", " ");
  }
}

export interface CardChangedEventArgs {
  longSKU: string;
  accessoryType: number;
  category: string;
  selected: boolean;
}
